/**
 * HiGrid 본문 셀 세로 병합 (연속 동일 값 → rowSpan)
 * - 컬럼 meta.mergeRows === true 인 열만 대상
 * - meta.mergeGroupKey 가 있으면 그룹이 바뀔 때 병합을 끊음 (예: requestUid)
 */

function normalizeMergeValue(v) {
  if (v == null) return '';
  return String(v);
}

/**
 * @param {Array<{ index: number, original: Record<string, unknown> }>} rows
 * @param {Array<{ id?: string, accessorKey?: string, field?: string, meta?: { mergeRows?: boolean, mergeGroupKey?: string } }>} columns
 * @returns {Record<string, Record<number, { rowSpan?: number, skip?: boolean }>> | null}
 */
export function buildBodyMergeMap(rows, columns) {
  const mergeCols = (columns || []).filter((c) => c?.meta?.mergeRows);
  if (!rows?.length || !mergeCols.length) return null;

  const map = {};
  for (const col of mergeCols) {
    const field = col.id ?? col.accessorKey ?? col.field;
    if (!field) continue;
    const groupKey = col.meta?.mergeGroupKey || null;
    const fieldMap = {};
    let i = 0;
    while (i < rows.length) {
      const base = rows[i].original || {};
      const baseVal = normalizeMergeValue(base[field]);
      const baseGroup = groupKey != null ? base[groupKey] : null;
      let j = i + 1;
      while (j < rows.length) {
        const cur = rows[j].original || {};
        if (groupKey != null && cur[groupKey] !== baseGroup) break;
        if (normalizeMergeValue(cur[field]) !== baseVal) break;
        j += 1;
      }
      const span = j - i;
      fieldMap[rows[i].index] = { rowSpan: span };
      for (let k = i + 1; k < j; k += 1) {
        fieldMap[rows[k].index] = { skip: true };
      }
      i = j;
    }
    map[field] = fieldMap;
  }
  return map;
}

/**
 * @returns {{ skip: boolean, rowSpan?: number }}
 */
export function resolveBodyMerge(bodyMergeMap, rowIndex, columnId) {
  if (!bodyMergeMap || columnId == null || rowIndex == null) {
    return { skip: false };
  }
  const info = bodyMergeMap[columnId]?.[rowIndex];
  if (!info) return { skip: false };
  if (info.skip) return { skip: true };
  return {
    skip: false,
    rowSpan: info.rowSpan > 1 ? info.rowSpan : undefined,
  };
}

/**
 * SheetJS `!merges` 범위 생성 (헤더 행 수만큼 row offset)
 * @param {object[]} rows - 원본 행 (AOA 데이터 행과 동일 순서)
 * @param {Array} columns - meta.mergeRows 포함 컬럼 정의
 * @param {string[]} exportFields - 내보내기 열 순서(필드명)
 * @param {{ headerRows?: number }} [opts]
 * @returns {Array<{ s: { r: number, c: number }, e: { r: number, c: number } }>}
 */
export function buildExcelMergeRanges(rows, columns, exportFields, opts = {}) {
  const headerRows = opts.headerRows ?? 1;
  const tanstackRows = (rows || []).map((original, index) => ({ index, original }));
  const map = buildBodyMergeMap(tanstackRows, columns);
  if (!map || !exportFields?.length) return [];

  const merges = [];
  exportFields.forEach((field, colIdx) => {
    const fieldMap = map[field];
    if (!fieldMap) return;
    Object.entries(fieldMap).forEach(([idxStr, info]) => {
      if (info.skip || !(info.rowSpan > 1)) return;
      const r = Number(idxStr) + headerRows;
      merges.push({
        s: { r, c: colIdx },
        e: { r: r + info.rowSpan - 1, c: colIdx },
      });
    });
  });
  return merges;
}

/**
 * 병합 범위에서 좌상단 외 셀 값을 비움 (엑셀 표시·복사 시 화면과 동일)
 * @param {any[][]} aoa
 * @param {Array<{ s: { r: number, c: number }, e: { r: number, c: number } }>} merges
 */
export function blankAoaOutsideMergeOrigins(aoa, merges) {
  if (!aoa?.length || !merges?.length) return aoa;
  const next = aoa.map((row) => (Array.isArray(row) ? row.slice() : row));
  for (const m of merges) {
    for (let r = m.s.r; r <= m.e.r; r += 1) {
      if (!next[r]) continue;
      for (let c = m.s.c; c <= m.e.c; c += 1) {
        if (r === m.s.r && c === m.s.c) continue;
        next[r][c] = '';
      }
    }
  }
  return next;
}

/** 컬럼 정의에 본문 세로 병합(meta.mergeRows)이 있는지 */
export function columnsHaveCellMerge(columns) {
  return (columns || []).some((c) => c?.meta?.mergeRows);
}
